import { ArrowDown, ArrowDownAZ, ArrowUp, ArrowUpAZ, ArrowUpDown, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

// Above this threshold the dropdown shows a typeahead filter.
const SEARCHABLE_THRESHOLD = 8

export type SortDirection = 'asc' | 'desc'

export type SortState = {
  column: string
  direction: SortDirection
}

export type FilterOption = {
  value: string
  label: string
}

type ColumnHeaderProps = {
  column: string
  label: string
  sort: SortState
  onSort: (column: string, direction: SortDirection) => void
  /** Optional — when provided, the dropdown also shows a "Filter by" section */
  filterLabel?: string
  filterOptions?: FilterOption[]
  filterValue?: string[]
  onFilterChange?: (next: string[]) => void
}

export function ColumnHeader({
  column,
  label,
  sort,
  onSort,
  filterLabel,
  filterOptions,
  filterValue = [],
  onFilterChange,
}: ColumnHeaderProps) {
  const isSorted = sort.column === column
  const ArrowIcon = isSorted ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  const canFilter = Boolean(filterOptions && filterOptions.length > 0 && onFilterChange)
  const hasActiveFilter = filterValue.length > 0

  const toggleArrow = () => {
    // Direct-click on the arrow: if not sorted → asc; else flip direction.
    if (!isSorted) return onSort(column, 'asc')
    onSort(column, sort.direction === 'asc' ? 'desc' : 'asc')
  }

  const toggleFilter = (value: string, checked: boolean) => {
    if (!onFilterChange) return
    if (checked) onFilterChange([...filterValue, value])
    else onFilterChange(filterValue.filter((v) => v !== value))
  }

  const [search, setSearch] = useState('')
  const searchable = canFilter && (filterOptions?.length ?? 0) > SEARCHABLE_THRESHOLD
  const visibleOptions = useMemo(() => {
    if (!filterOptions) return []
    if (!search) return filterOptions
    const needle = search.toLowerCase()
    return filterOptions.filter((o) => o.label.toLowerCase().includes(needle))
  }, [filterOptions, search])

  return (
    <div className="inline-flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            'inline-flex items-center gap-1 rounded-sm px-1 text-xs font-medium uppercase tracking-widest transition-colors',
            'hover:text-foreground focus-visible:outline-none',
            isSorted || hasActiveFilter ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {label}
          {hasActiveFilter && (
            <span className="ml-1 text-foreground">({filterValue.length})</span>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="min-w-[12rem]">
          <DropdownMenuItem onSelect={() => onSort(column, 'asc')}>
            <ArrowUpAZ className="h-3.5 w-3.5" />
            Sort ascending
            {isSorted && sort.direction === 'asc' && (
              <span className="ml-auto text-xs text-muted-foreground">Active</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onSort(column, 'desc')}>
            <ArrowDownAZ className="h-3.5 w-3.5" />
            Sort descending
            {isSorted && sort.direction === 'desc' && (
              <span className="ml-auto text-xs text-muted-foreground">Active</span>
            )}
          </DropdownMenuItem>

          {canFilter && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{filterLabel ?? 'Filter'}</DropdownMenuLabel>

              {searchable && (
                <div className="relative mx-1 mb-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()} // keep letter keys in the field
                    className={cn(
                      'h-7 w-full rounded-sm border border-border bg-background pl-7 pr-2 text-xs',
                      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    )}
                  />
                </div>
              )}

              <div className="max-h-56 overflow-auto">
                {visibleOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">No matches</div>
                ) : (
                  visibleOptions.map((o) => (
                    <DropdownMenuCheckboxItem
                      key={o.value}
                      checked={filterValue.includes(o.value)}
                      onCheckedChange={(checked) => toggleFilter(o.value, Boolean(checked))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {o.label}
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </div>

              {hasActiveFilter && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => onFilterChange!([])}
                    className="text-muted-foreground"
                  >
                    Clear {filterLabel?.toLowerCase() ?? 'filter'}
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        type="button"
        onClick={toggleArrow}
        aria-label={`Sort by ${label}`}
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded-sm transition-colors',
          'hover:bg-muted',
          isSorted ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <ArrowIcon className="h-3 w-3" />
      </button>
    </div>
  )
}
